import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';

const TermsAndConditions = () => {
  return (
    <>
      <SEO
        title="Terms and Conditions"
        description="Theatre booking platform terms and conditions - the rules, guidelines, and agreements for using our services."
        keywords={['terms and conditions', 'user agreement', 'theatre booking', 'legal terms']}
        url="/terms-and-conditions"
      />

      <PageHero
        title="Terms and Conditions"
        subtitle="Please read these terms carefully before using our services"
        image="/gallery/image.jpg "
      />

      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-md">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="prose prose-lg max-w-none"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Terms and Conditions</h2>

              <p className="mb-6 text-gray-700">
                Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>

              <p className="mb-4 text-gray-700">
                Welcome to our theatre booking platform. These Terms and Conditions ("Terms") govern your use of our website, mobile applications, and services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h3>

              <p className="mb-4 text-gray-700">
                By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, and any additional terms or policies that we may provide. If you are using our Services on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. Account Registration</h3>

              <p className="mb-4 text-gray-700">
                To access certain features of our Services, you may need to create an account. When you register for an account, you agree to:
              </p>

              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Be responsible for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>

              <p className="mb-4 text-gray-700">
                We reserve the right to suspend or terminate your account if you violate these Terms or if we determine, in our sole discretion, that your use of the Services is inappropriate or harmful.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Booking and Reservations</h3>

              <p className="mb-4 text-gray-700">
                Our Services allow you to book and reserve theatrical venues and related services. When you make a booking, you agree to:
              </p>

              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Provide accurate and complete information for all attendees</li>
                <li>Pay all applicable fees and charges</li>
                <li>Comply with the venue's specific terms and conditions</li>
                <li>Adhere to the cancellation and refund policies outlined in Section 4</li>
              </ul>

              <p className="mb-4 text-gray-700">
                We act as an intermediary between you and the theatrical venues. While we strive to ensure accuracy, we cannot guarantee that all information on our platform (including pricing, availability, and venue details) is complete or current. The final booking confirmation from the venue is the governing document for your reservation.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Cancellation and Refund Policy</h3>

              <p className="mb-4 text-gray-700">
                Our cancellation and refund policy is as follows:
              </p>

              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Cancellations made 48 hours or more before the scheduled booking time are eligible for a full refund</li>
                <li>Cancellations made between 24-48 hours before the scheduled booking time are eligible for a 50% refund</li>
                <li>Cancellations made less than 24 hours before the scheduled booking time are not eligible for a refund</li>
                <li>No-shows are not eligible for a refund</li>
              </ul>

              <p className="mb-4 text-gray-700">
                Refunds will be processed using the original payment method and may take 5-7 business days to appear on your account. Some venues may have specific cancellation policies that override our standard policy. In such cases, the venue's policy will be clearly displayed during the booking process.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. User Conduct</h3>

              <p className="mb-4 text-gray-700">
                When using our Services, you agree not to:
              </p>

              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Use our Services for any illegal or unauthorized purpose</li>
                <li>Interfere with or disrupt our Services</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Collect or harvest user data without permission</li>
                <li>Transmit any viruses, malware, or other harmful code</li>
                <li>Engage in fraudulent activities</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Intellectual Property</h3>

              <p className="mb-4 text-gray-700">
                All content on our Services, including but not limited to text, graphics, logos, images, audio clips, digital downloads, and software, is owned by us or our licensors and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, modify, distribute, or otherwise use any content from our Services without our prior written consent.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Limitation of Liability</h3>

              <p className="mb-4 text-gray-700">
                To the maximum extent permitted by law, we and our affiliates, officers, employees, agents, partners, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, or goodwill, arising out of or in connection with your access to or use of our Services.
              </p>

              <p className="mb-4 text-gray-700">
                In no event shall our total liability to you for all claims arising from or relating to these Terms or your use of our Services exceed the amount paid by you to us for the Services during the twelve (12) months preceding the event giving rise to the liability.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Indemnification</h3>

              <p className="mb-4 text-gray-700">
                You agree to indemnify, defend, and hold harmless us and our affiliates, officers, employees, agents, partners, and licensors from and against any claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from or relating to:
              </p>

              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Your use of our Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another person or entity</li>
                <li>Your violation of any applicable laws or regulations</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. Modifications to Terms</h3>

              <p className="mb-4 text-gray-700">
                We may modify these Terms at any time by posting the revised Terms on our website. Your continued use of our Services after any such changes constitutes your acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using our Services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Governing Law</h3>

              <p className="mb-4 text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any legal action or proceeding arising out of or relating to these Terms shall be brought exclusively in the courts of Bangalore, Karnataka, and you consent to the personal jurisdiction of such courts.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Severability</h3>

              <p className="mb-4 text-gray-700">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect and enforceable.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">12. Contact Information</h3>

              <p className="mb-4 text-gray-700">
                If you have any questions about these Terms, please contact us at:
              </p>

              <div className="mb-6 text-gray-700">
                <p>Email: <a href="mailto:happybubble36@gmail.com" className="text-indigo-600 hover:text-indigo-500">happybubble36@gmail.com</a></p>
                <p>Phone: <a href="tel:+919880346464" className="text-indigo-600 hover:text-indigo-500">+91 9880346464</a>, <a href="tel:+919986713636" className="text-indigo-600 hover:text-indigo-500">+91 9986713636</a></p>
                <p>Address: 16, 80 Feet Rd, near Wipro Park, opposite SBI BANK, 1st Block Koramangala, Koramangala, Bengaluru, Karnataka 560034</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsAndConditions; 